import discountQueries from '@apis/queries/discountQueries';
import BlankEtc from '@components/blank/BlankEtc';
import Column from '@components/Column';
import { DiscountCouponRow } from '@models/discount';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQuery } from '@tanstack/react-query';
import { filterOrderCoupons } from '@utils/couponUtils';
import { useTranslation } from 'react-i18next';

import CouponCard from './CouponCard';

interface Props {
    selectedCouponId: string | null;
    onToggle: (couponId: string) => void;
}

/** 주문(장바구니) 쿠폰 목록. 한 장만 고를 수 있다 */
function OrderCouponList({ selectedCouponId, onToggle }: Props) {
    const { t } = useTranslation('order');
    const state = useCalculatedRequest();
    const {
        shopId, orderLines, totalAmount, prepaidCardAmount, giftAmount,
    } = state;
    const { data } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });
    const coupons = filterOrderCoupons(data?.coupons, orderLines, totalAmount);

    const isDisabled = (coupon: DiscountCouponRow) => {
        if (selectedCouponId === coupon.id) {
            return false;
        }
        const { discountValue, discountMethod } = coupon;
        let resolvedDiscountValue = discountValue!;
        if (discountMethod === 'RATE') {
            resolvedDiscountValue = totalAmount * (discountValue! / 100);
        }
        return totalAmount - prepaidCardAmount - giftAmount < resolvedDiscountValue;
    };
    // 사용할 수 없는 쿠폰은 맨 아래로
    const sorted = [
        ...coupons.filter((it) => !isDisabled(it)),
        ...coupons.filter((it) => isDisabled(it)),
    ];

    if (sorted.length === 0) {
        return (
            <BlankEtc>
                {t('couponPage.empty')}
            </BlankEtc>);
    }
    return (
        <Column fullWidth spacing={2}>
            {sorted.map((coupon) => (
                <CouponCard key={coupon.id}
                    coupon={coupon}
                    isSelected={selectedCouponId === coupon.id}
                    disabled={isDisabled(coupon)}
                    onToggle={() => onToggle(coupon.id)} />
            ))}
        </Column>
    );
}

export default OrderCouponList;
